import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable, map, firstValueFrom} from 'rxjs';
import { Event, Vens } from '../model/home.model';
import { VtnViewModel } from '../model/vtns.model';
import { PlatformViewModel } from '../model/platform.model';
import { Resource } from '../model/resources.model';
import { ChannelPowertypes, MarketContexts, RidViewModel } from '../model/rids.model';
import { MetadataReportViewModel } from '../model/metadataReports.model';
import {Events, RegistrationDto, VenViewModel} from '../model/vens.model';
import { EventViewModel  as events} from '../model/events.model';
import { Cert } from '../model/certs.model';
import { Payloads } from '../model/payloads.model';
import { Group } from '../model/group.model';
import { MarketContextViewModel } from '../model/market-context.model';
import { RegisteredReportViewModel } from '../model/registeredReports.model';
import { PowerTypes } from '../model/powerTypes.model';
import { ChannelViewModel } from '../model/channel.model';
import { SchedulerTask } from '../model/scheduler.model';
import {environment} from "../../environments/environment";
import {LoginDto, LoginViewModel, ValidateSessionViewModel} from "../model/login.model";

export type CustomHttpParams = Record<string, string | number | boolean | object>;

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly apiUrl = environment.venway3ApiHost;

  constructor(private httpClient: HttpClient) {}

  private httpParamsBuilder(
    queryParams?: CustomHttpParams,
  ) {
    let params = new HttpParams();

    if (!queryParams) {
      return params;
    }

    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== undefined && value !== null) {
        const paramValue = typeof value === 'object' ? JSON.stringify(value) : value.toString();
        params = params.append(key, paramValue);
      }
    }

    return params;
  }

  async countItems(modelType: string, queryParams?: CustomHttpParams): Promise<number> {
    const params = this.httpParamsBuilder(queryParams);
    const url = this.apiUrl +  `/api/${modelType}/count`
    const observable = this.httpClient.get<ApiResponse<number>>(url, { params }).pipe(
      map((response) => response.data)
    );

    return firstValueFrom(observable);
  }

  // VENS
  public findVens(queryParams?: CustomHttpParams): Observable<VenViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return (this.httpClient.get(this.apiUrl + "/api/ven", { params: params })).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  public editVen(editVen: any): Observable<[]> {
    return this.httpClient.put(this.apiUrl + '/api/ven', editVen).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public deleteVen(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/ven',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public getEventPollHistory(queryParams?: CustomHttpParams): Observable<Events[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/event_poll_history', { params: params }).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public togglePrimary(id: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/ven/toggle_primary', id).pipe(map((response: any) => {
      return response.data;
    }));
  }

  // VTN
  public findVtn(queryParams?: CustomHttpParams): Observable<VtnViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/vtn', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public findVtnStatus(id: any): Observable<[]> {
    let params = new HttpParams().set('vtn_id', id);
    return this.httpClient.get(this.apiUrl + '/api/status',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public putVtnStatus(vtn_status: any): Observable<[]> {
    return this.httpClient.put(this.apiUrl + '/api/status',  vtn_status).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public deleteVtn(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/vtn',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public getVtn(queryParams?: CustomHttpParams): Observable<VtnViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/vtn', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public editVtn(editVen: any): Observable<[]> {
    return this.httpClient.put(this.apiUrl + '/api/vtn', editVen).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public createVtn(editVen: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/vtn', editVen).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public login(credentials: LoginDto): Observable<LoginViewModel> {
    return this.httpClient.post(this.apiUrl + '/api/login', credentials).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public validateSession(token: string): Observable<ValidateSessionViewModel> {
    let params = new HttpParams().set('enoc_session', token);
    return this.httpClient.get(this.apiUrl + '/api/session/validate', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //EVENTS
  public findEvents(event: number): Observable<Event[]> {
    let params = new HttpParams().set('depth', event);
    return this.httpClient.get(this.apiUrl + '/api/event', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //PLATFORMS
  public findPlatforms(platform: number): Observable<PlatformViewModel[]> {
    let params = new HttpParams().set('depth', platform);
    return this.httpClient.get(this.apiUrl + '/api/platform', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //GROUPS
  public findGroups(platform: number): Observable<PlatformViewModel[]> {
    let params = new HttpParams().set('depth', platform);
    return this.httpClient.get(this.apiUrl + '/api/group', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //RESOURCES
  public findResources(resource: number): Observable<Resource[]> {
    let params = new HttpParams().set('depth', resource);
    return this.httpClient.get(this.apiUrl + '/api/resource', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //MARKET CONTEXT
  public findMarketContext(market_context: number): Observable<MarketContexts[]> {
    let params = new HttpParams().set('depth', market_context);
    return this.httpClient.get(this.apiUrl + '/api/market_context', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //METADA REPORT
  public findMetadataReport(metadata_report: number): Observable<MetadataReportViewModel[]> {
    let params = new HttpParams().set('depth', metadata_report);
    return this.httpClient.get(this.apiUrl + '/api/metadata_report', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //REGISTRATION
  public findRegistration(queryParams?: CustomHttpParams): Observable<RegistrationDto[]> {
    const params = this.httpParamsBuilder(queryParams);
    return (this.httpClient.get(this.apiUrl + "/api/registration", { params: params })).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  public deleteRegistration(id: any): Observable<RegistrationDto[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/registration',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public createRegistration(createRegistration: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/registration', createRegistration).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //CERT
  public findCert(queryParams?: CustomHttpParams): Observable<Cert[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/cert', { params: params }).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteCert(id: any): Observable<Cert[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/cert',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editCert(certs: any): Observable<Cert[]> {
    return this.httpClient.put(this.apiUrl + '/api/cert', certs).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public createCert(cert: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/cert', cert).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //PAYLOADS
  public getPayloads(queryParams?: CustomHttpParams): Observable<Payloads[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/payload', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  // EVENTS
  public getEvents(queryParams?: CustomHttpParams): Observable<events[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/event', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //GROUP
  public getGroups(queryParams?: CustomHttpParams): Observable<Group[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/group', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editGroups(group: any): Observable<Group[]> {
    return this.httpClient.put(this.apiUrl + '/api/group', group).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createGroup(group: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/group', group).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteGroup(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/group',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //RESOURCES
  public getResources(queryParams?: CustomHttpParams): Observable<Resource[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/resource', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editResources(resources: any): Observable<Resource[]> {
    return this.httpClient.put(this.apiUrl + '/api/resource', resources).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createResources(resource: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/resource', resource).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteResources(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/resource',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //MARKET SERVICE
  public getMarketContext(queryParams?: CustomHttpParams): Observable<MarketContextViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/market_context', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteMarketContext(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/market_context',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editMarketContext(market_context: any): Observable<MarketContextViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/market_context', market_context).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createMarketContext(market_context: any): Observable<MarketContextViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/market_context', market_context).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //PLATFORM
  public getPlatform(queryParams?: CustomHttpParams): Observable<PlatformViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/platform', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editPlatform(platform: any): Observable<PlatformViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/platform', platform).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createPlatform(platform: any): Observable<PlatformViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/platform', platform).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deletePlatform(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/platform',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //METADATA REPORT
  public getMetadataReports(queryParams?: CustomHttpParams): Observable<MetadataReportViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/metadata_report', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editMetadataReport(metadaReport: any): Observable<MetadataReportViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/metadata_report', metadaReport).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createMetadataReport(metadaReport: any): Observable<MetadataReportViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/metadata_report', metadaReport).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteMetadataReport(id: any): Observable<RegisteredReportViewModel[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/metadata_report', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //REGISTERED REPORTS
  public getRegisteredReports(queryParams?: CustomHttpParams): Observable<RegisteredReportViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/registered_report', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editRegisteredReports(registeredReports: any): Observable<RegisteredReportViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/registered_report', registeredReports).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createRegisteredReports(registeredReports: any): Observable<RegisteredReportViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/registered_report', registeredReports).pipe(map((response: any) => {
      return response.data;
    }));

  }
  public deleteRegisteredReports(id: any): Observable<RegisteredReportViewModel[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/registered_report', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));

  }
  // REPORT HISTORY
  public getReportHistory(queryParams?: CustomHttpParams): Observable<RegisteredReportViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/report_history', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //RIDS
  public getRids(queryParams?: CustomHttpParams): Observable<RidViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/rid', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteRids(id: any): Observable<RidViewModel[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/rid', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createRids(rids: RidViewModel): Observable<RidViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/rid', rids).pipe(map((response: any) => {
      return response.data;
    }));

  }
  public editRids(registeredReports: any): Observable<RidViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/rid', registeredReports).pipe(map((response: any) => {
      return response.data;
    }));

  }
  public potentialRids(by: string, data_source: string, depth: number, operator: string, program: string, id?: string): Observable<RidViewModel[]> {
    let params = new HttpParams();
    if (id) {
      params = params.append("id", id);
    }
    params = params.set("depth", depth).set("by", by).set("data_source", data_source).set("operator", operator).set("program", program);
    return this.httpClient.get(this.apiUrl + '/api/potential_rid', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));

  }
  public createPotentialRids(rids: RidViewModel): Observable<RidViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/potential_rid', rids).pipe(map((response: any) => {
      return response.data;
    }));
  }
    //POWER TYPE
  public getPowerType(queryParams?: CustomHttpParams): Observable<[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/power_type', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deletePowerTypes(id: any): Observable<RidViewModel[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/power_type', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public getPowertypesDescriptions(): Observable<[]> {
    return this.httpClient.get(this.apiUrl + '/api/param/powertypes_descriptions/',  ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public getItemUnits(powertypes_description?: any): Observable<[]> {
    let params = new HttpParams();
    if(powertypes_description){
      params = params.set('powertypes_description', powertypes_description);
    }
    return this.httpClient.get(this.apiUrl + '/api/param/item_units/', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public getSiScaleCode(): Observable<[]> {
    return this.httpClient.get(this.apiUrl + '/api/param/si_scale_code/' ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editPowerType(powertype: any): Observable<PowerTypes[]> {
    return this.httpClient.put(this.apiUrl + '/api/power_type', powertype).pipe(map((response: any) => {
      return response.data;
    }));

  }
  public createPowerType(powertype: any): Observable<PowerTypes[]> {
    return this.httpClient.post(this.apiUrl + '/api/power_type', powertype).pipe(map((response: any) => {
      return response.data;
    }));

  }
    //CHANNEL
  public getChannel(queryParams?: CustomHttpParams): Observable<ChannelViewModel[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/channel', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }

  public editChannel(channel: any): Observable<ChannelViewModel[]> {
    return this.httpClient.put(this.apiUrl + '/api/channel', channel).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteChannel(id: any): Observable<ChannelViewModel[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/channel', {params: params} ).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createChannel(channel: any): Observable<ChannelViewModel[]> {
    return this.httpClient.post(this.apiUrl + '/api/channel', channel).pipe(map((response: any) => {
      return response.data;
    }));
  }
    //CHANNEL
  public getConversion(queryParams?: CustomHttpParams): Observable<ChannelPowertypes[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/conversion', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  //ACTION TAB
  public requestEvent(id: any): Observable<[]> {
    let params = new HttpParams().set('ven_id', id);
    return this.httpClient.get(this.apiUrl + '/api/action/request_event', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public metadataReportMessage(id: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/action/generate_metadata_report_message', id).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public updateReportMessage(reportMessage: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/action/generate_update_report_message', reportMessage).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public granularities(): Observable<[]> {
    return this.httpClient.get(this.apiUrl + '/api/param/granularities/').pipe(map((response: any) => {
      return response.data;
    }));
  }
  public registerReports(registerReport: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/action/register_reports', registerReport).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public sendReport(report_id: any,ven_id: any): Observable<[]> {
    let params = new HttpParams().set('report_id', report_id).set('ven_id', ven_id);
    return this.httpClient.get(this.apiUrl + '/api/action/send_report', { params: params }).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public backFillReport(backFill: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/action/backfill_report', backFill).pipe(map((response: any) => {
      return response.data;
    }));
  }
  //SYSTEM PROPERTIES
  public getSystemProperties(depth: any): Observable<[]> {
    let params = new HttpParams().set('depth', depth);
    return this.httpClient.get(this.apiUrl + '/api/system_property', {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteSystemProperties(id: any): Observable<[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/system_property',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editSystemProperties(system: any): Observable<[]> {
    return this.httpClient.put(this.apiUrl + '/api/system_property', system).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createSystemProperties(system: any): Observable<[]> {
    return this.httpClient.post(this.apiUrl + '/api/system_property', system).pipe(map((response: any) => {
      return response.data;
    }));
  }

  //Scheduler
  public getSchedulerTasks(queryParams?: CustomHttpParams): Observable<SchedulerTask[]> {
    const params = this.httpParamsBuilder(queryParams);
    return this.httpClient.get(this.apiUrl + '/api/scheduler_tasks', { params: params }).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public deleteSchedulerTasks(id: any): Observable<SchedulerTask[]> {
    let params = new HttpParams().set('id', id);
    return this.httpClient.delete(this.apiUrl + '/api/scheduler_tasks',  {params: params}).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public editSchedulerTaskss(system: any): Observable<SchedulerTask[]> {
    return this.httpClient.put(this.apiUrl + '/api/scheduler_tasks', system).pipe(map((response: any) => {
      return response.data;
    }));
  }
  public createSchedulerTasks(system: any): Observable<SchedulerTask[]> {
    return this.httpClient.post(this.apiUrl + '/api/scheduler_tasks', system).pipe(map((response: any) => {
      return response.data;
    }));
  }
}
